import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import styled from 'styled-components';
import Header from './components/Header';
import Home from './pages/Home';
import CVAnalyzer from './pages/CVAnalyzer';
import CVGenerator from './pages/CVGenerator';
import InterviewSimulator from './pages/InterviewSimulator';
import Footer from './components/Footer';
import CareerCoach from './pages/CareerCoach';
import AdminLogin from './pages/AdminLogin';
import JobMatch from './pages/JobMatch';
import Admin from './pages/Admin';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFail from './pages/PaymentFail';
import BillingSuccess from './pages/BillingSuccess';
import Dashboard from './pages/Dashboard';
import { FREE_LIMIT_EVENT } from './api';
import Activate from './pages/Activate';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import GlobalJobs from './pages/GlobalJobs';
import PremiumPass from './pages/PremiumPass';
import CookiePolicy from './pages/CookiePolicy';
import CookieBanner from './components/CookieBanner';

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const LimitOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const LimitCard = styled.div`
  background: rgba(255, 255, 255, 0.97);
  border-radius: 18px;
  padding: 1.75rem 2rem;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 25px 50px rgba(15, 23, 42, 0.35);
  text-align: center;
`;

const LimitTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #111827;
`;

const LimitText = styled.p`
  font-size: 0.95rem;
  color: #4b5563;
  margin-bottom: 1.25rem;
`;

const LimitButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 0.6rem 1.4rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #ffffff;
`;

const LimitLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 0.6rem 1.4rem;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  background: #111827;
  color: #ffffff;
`;

function App() {
  const [showLimitOverlay, setShowLimitOverlay] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');

  useEffect(() => {
    const handler = (event) => {
      const msg =
        (event && event.detail) ||
        'Bu ay ücretsiz kullanım hakkın doldu. Premium Pass satın alarak devam edebilirsin.';
      setLimitMessage(msg);
      setShowLimitOverlay(true);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(FREE_LIMIT_EVENT, handler);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(FREE_LIMIT_EVENT, handler);
      }
    };
  }, []);

  return (
    <Router>
      <AppContainer>
        <Header />
        <MainContent>
          {showLimitOverlay && (
            <LimitOverlay onClick={() => setShowLimitOverlay(false)}>
              <LimitCard onClick={(e) => e.stopPropagation()}>
                <LimitTitle>Günlük kullanım hakkınız doldu</LimitTitle>
                <LimitText>{limitMessage}</LimitText>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                  <LimitLink to="/pricing">Premium Pass</LimitLink>
                  <LimitButton type="button" onClick={() => setShowLimitOverlay(false)}>
                    Tamam
                  </LimitButton>
                </div>
              </LimitCard>
            </LimitOverlay>
          )}
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/activate" element={<Activate />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/app" element={<Dashboard />} />
            <Route path="/odeme-basarili" element={<PaymentSuccess />} />
            <Route path="/odeme-basarisiz" element={<PaymentFail />} />
            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/cv-analyzer" element={<CVAnalyzer />} />
            <Route path="/cv-generator" element={<CVGenerator />} />
            <Route path="/interview-simulator" element={<InterviewSimulator />} />
            <Route path="/career-coach" element={<CareerCoach />} />
            <Route path="/job-match" element={<JobMatch />} />
            <Route path="/global-jobs" element={<GlobalJobs />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/premium-pass" element={<PremiumPass />} />
            <Route path="/pricing" element={<PremiumPass />} />
            <Route path="/cerez-politikasi" element={<CookiePolicy />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/login" element={<AdminLogin />} />
          </Routes>
        </MainContent>
        <CookieBanner />
        <Footer />
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#4ade80',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </AppContainer>
    </Router>
  );
}

export default App;
